import {
    ChevronRight,
    Folder,
    FolderMinus,
    FolderPlus,
    Pencil,
    Pin,
    Plus,
    Share,
    Trash2
} from 'lucide-react';

import { Translator } from '../i18n';

export interface FolderData {
    id: string;
    name: string;
    level: number;
}

export interface ThreadMenuItemsProps {
    onDelete: () => void;
    onRename: () => void;
    onAddToProject?: (folderId: string) => void;
    onCreateNewProject?: () => void;
    onRemoveFromProject?: () => void;
    onPinToOrg?: () => void;
    onShareThread?: () => void;
    folders?: FolderData[];
    isInProject?: boolean;
}

export interface MenuItemProps {
    onClick: (e: React.MouseEvent) => void;
    icon: React.ReactNode;
    children: React.ReactNode;
    className?: string;
    destructive?: boolean;
    style?: React.CSSProperties;
    onMouseEnter?: (e: React.MouseEvent) => void;
}

export interface MenuSeparatorProps {
    className?: string;
}

export interface SubmenuProps {
    trigger: React.ReactNode;
    children: React.ReactNode;
    onMouseEnter?: (e: React.MouseEvent) => void;
    onMouseLeave?: (e: React.MouseEvent) => void;
}

// Abstract interface for different menu implementations
export interface MenuRenderer {
    MenuItem: React.ComponentType<MenuItemProps>;
    MenuSeparator: React.ComponentType<MenuSeparatorProps>;
    Submenu?: React.ComponentType<SubmenuProps>;
}

export function useThreadMenuItems({
    onDelete,
    onRename,
    onAddToProject,
    onCreateNewProject,
    onRemoveFromProject,
    onPinToOrg,
    onShareThread,
    folders = [],
    isInProject = false
}: ThreadMenuItemsProps) {
    const renderMenuItems = (renderer: MenuRenderer) => {
        const { MenuItem, MenuSeparator, Submenu } = renderer;

        return [
            // Rename

            <MenuItem
                key="rename"
                onClick={(e) => {
                    e.stopPropagation();
                    onRename();
                }}
                icon={<Pencil />}
            >
                <Translator path="threadHistory.thread.menu.rename" />
            </MenuItem>,
            // Share Thread
            ...(onShareThread
                ? [
                      <MenuItem
                          key="share-thread"
                          onClick={(e) => {
                              e.stopPropagation();
                              onShareThread();
                          }}
                          icon={<Share />}
                      >
                          Share
                      </MenuItem>
                  ]
                : []),

            // Pin to organization (if available)
            ...(onPinToOrg
                ? [
                      <MenuItem
                          key="pin-to-org"
                          onClick={(e) => {
                              e.stopPropagation();
                              onPinToOrg();
                          }}
                          icon={<Pin />}
                      >
                          Pin to organization
                      </MenuItem>
                  ]
                : []),

            // Add/Move to Project (if available)
            ...(onAddToProject
                ? [
                      Submenu ? (
                          <Submenu
                              key="add-to-project"
                              trigger={
                                  <>
                                      <FolderPlus />
                                      {isInProject
                                          ? 'Move to project'
                                          : 'Add to project'}
                                  </>
                              }
                          >
                              {folders.length > 0 ? (
                                  <>
                                      <MenuItem
                                          onClick={(e) => {
                                              e.stopPropagation();
                                              onCreateNewProject?.();
                                          }}
                                          onMouseEnter={() => {
                                              /* Keep submenu open */
                                          }}
                                          icon={<Plus />}
                                          className="border-border border-b"
                                      >
                                          New project
                                      </MenuItem>
                                      {folders.map((folder) => (
                                          <MenuItem
                                              key={folder.id}
                                              onClick={(e) => {
                                                  e.stopPropagation();
                                                  onAddToProject(folder.id);
                                              }}
                                              onMouseEnter={() => {
                                                  /* Keep submenu open */
                                              }}
                                              icon={
                                                  <Folder className="text-muted-foreground" />
                                              }
                                              style={{
                                                  paddingLeft: `${
                                                      12 + folder.level * 16
                                                  }px`
                                              }}
                                              className="flex"
                                          >
                                              <span className="flex-1 w-0 min-w-0 truncate grow">
                                                  {folder.name}
                                              </span>
                                          </MenuItem>
                                      ))}
                                  </>
                              ) : (
                                  <MenuItem
                                      onClick={(e) => {
                                          e.stopPropagation();
                                          onCreateNewProject?.();
                                      }}
                                      onMouseEnter={() => {
                                          /* Keep submenu open */
                                      }}
                                      icon={<Plus />}
                                  >
                                      Create first project
                                  </MenuItem>
                              )}
                          </Submenu>
                      ) : null
                  ]
                : []),

            // Remove from Project (if in project)
            ...(isInProject && onRemoveFromProject
                ? [
                      <MenuItem
                          key="remove-from-project"
                          onClick={(e) => {
                              e.stopPropagation();
                              onRemoveFromProject();
                          }}
                          icon={<FolderMinus />}
                      >
                          Remove from project
                      </MenuItem>
                  ]
                : []),

            // Delete
            <MenuSeparator key="sep5" />,
            <MenuItem
                key="delete"
                onClick={(e) => {
                    e.stopPropagation();
                    onDelete();
                }}
                icon={<Trash2 />}
                destructive
            >
                <Translator path="threadHistory.thread.menu.delete" />
            </MenuItem>
        ];
    };

    return { renderMenuItems };
}
